import { memo, useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';

// ============================================================
// Types + constants
// ============================================================

type TicketStatus = 'backlog' | 'in_progress' | 'qa' | 'done';
type TicketPriority = 'p0' | 'p1' | 'p2' | 'p3';
type TicketType = 'bug' | 'feature' | 'chore' | 'doc';

type Comment = { author: string; text: string; createdAt: string };

type Ticket = {
  id: string;
  type: TicketType;
  title: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  assignee: string;
  labels: string[];
  dueDate?: string | null;
  comments: Comment[];
  createdAt: string;
  updatedAt: string;
};

type View = 'overview' | 'board' | 'list';

// ============================================================
// Minimal markdown renderer (bold, italic, code, links, lists, headers)
// Returns HTML string safe for use with dangerouslySetInnerHTML.
// Escapes HTML entities first to prevent XSS.
// ============================================================

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function renderMarkdown(text: string): string {
  if (!text) return '';
  const lines = text.split('\n');
  const out: string[] = [];
  let inList = false;
  let inCode = false;
  const closeList = () => { if (inList) { out.push('</ul>'); inList = false; } };

  for (const raw of lines) {
    const line = raw;
    // Code fence
    if (line.trim().startsWith('```')) {
      closeList();
      if (inCode) { out.push('</code></pre>'); inCode = false; }
      else { out.push('<pre class="bg-slate-100 rounded-md px-3 py-2 text-xs overflow-x-auto my-2"><code>'); inCode = true; }
      continue;
    }
    if (inCode) { out.push(escapeHtml(line)); continue; }

    const trimmed = line.trim();
    if (!trimmed) { closeList(); out.push(''); continue; }

    // Headers
    const h = trimmed.match(/^(#{1,3})\s+(.+)$/);
    if (h) {
      closeList();
      const level = h[1].length;
      const content = inlineMd(h[2]);
      const cls = level === 1 ? 'text-base font-extrabold mt-3 mb-1' : level === 2 ? 'text-sm font-extrabold mt-2 mb-1' : 'text-xs font-bold uppercase tracking-wider text-slate-600 mt-2 mb-1';
      out.push(`<h${level} class="${cls}">${content}</h${level}>`);
      continue;
    }

    // List item
    const li = trimmed.match(/^[-*]\s+(.+)$/);
    if (li) {
      if (!inList) { out.push('<ul class="list-disc pl-5 my-1 space-y-0.5">'); inList = true; }
      out.push(`<li>${inlineMd(li[1])}</li>`);
      continue;
    }

    closeList();
    out.push(`<p class="my-1">${inlineMd(line)}</p>`);
  }
  closeList();
  if (inCode) out.push('</code></pre>');
  return out.join('\n');
}

function inlineMd(text: string): string {
  // Order matters here — process in a sequence that doesn't need lookbehind
  // assertions (unsupported on older iOS Safari / mobile browsers).
  const LINK_TOKEN = '\x00LINK\x00';
  const links: string[] = [];

  let s = escapeHtml(text);

  // 1. Pull [text](url) explicit links OUT and replace with tokens so later
  //    passes don't match inside them.
  s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, (_m, label, url) => {
    links.push(`<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">${label}</a>`);
    return LINK_TOKEN + (links.length - 1) + LINK_TOKEN;
  });
  // 2. Bare URLs become links too — safe to match freely since explicit links
  //    are already tokenized.
  s = s.replace(/(https?:\/\/[^\s<]+)/g, (_m, url) => {
    links.push(`<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">${url}</a>`);
    return LINK_TOKEN + (links.length - 1) + LINK_TOKEN;
  });
  // 3. Inline code
  s = s.replace(/`([^`]+)`/g, '<code class="font-mono text-xs bg-slate-100 px-1 rounded">$1</code>');
  // 4. Bold **…** first so the italic pass can't eat it.
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-bold">$1</strong>');
  // 5. Italic *…* — because step 4 removed the ** patterns, any surviving *x*
  //    is safe to match without a lookbehind.
  s = s.replace(/\*([^*\n]+)\*/g, '<em>$1</em>');
  s = s.replace(/\b_([^_\n]+)_\b/g, '<em>$1</em>');

  // 6. Restore link tokens
  s = s.replace(new RegExp(LINK_TOKEN + '(\\d+)' + LINK_TOKEN, 'g'), (_m, i) => links[Number(i)] || '');
  return s;
}

const API_BASE = import.meta.env.DEV ? 'http://localhost:8000/api' : '/api';
const TOKEN_KEY = 'treadai_project_token';
const AUTHOR_KEY = 'treadai_project_author';
const VIEW_KEY = 'treadai_project_view';
const LAST_TYPE_KEY = 'treadai_project_last_type';
const LAST_PRIORITY_KEY = 'treadai_project_last_priority';

const STALE_DAYS = 14;

const STATUS_META: Record<TicketStatus, { label: string; dot: string; soft: string }> = {
  backlog:     { label: 'Backlog',     dot: '#94a3b8', soft: '#f1f5f9' },
  in_progress: { label: 'In Progress', dot: '#2563eb', soft: '#dbeafe' },
  qa:          { label: 'QA',          dot: '#7c3aed', soft: '#ede9fe' },
  done:        { label: 'Done',        dot: '#059669', soft: '#d1fae5' },
};
const STATUS_ORDER: TicketStatus[] = ['backlog', 'in_progress', 'qa', 'done'];

const PRIORITY_META: Record<TicketPriority, { label: string; color: string; soft: string }> = {
  p0: { label: 'P0', color: '#dc2626', soft: '#fef2f2' },
  p1: { label: 'P1', color: '#ea580c', soft: '#fff7ed' },
  p2: { label: 'P2', color: '#ca8a04', soft: '#fefce8' },
  p3: { label: 'P3', color: '#64748b', soft: '#f1f5f9' },
};

const TYPE_META: Record<TicketType, { label: string; color: string }> = {
  bug:     { label: 'Bug',     color: '#dc2626' },
  feature: { label: 'Feature', color: '#2563eb' },
  chore:   { label: 'Chore',   color: '#64748b' },
  doc:     { label: 'Doc',     color: '#7c3aed' },
};

// ============================================================
// Small helpers
// ============================================================

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function isStale(t: Ticket): boolean {
  if (t.status === 'done') return false;
  const days = (Date.now() - new Date(t.updatedAt).getTime()) / (1000 * 60 * 60 * 24);
  return days > STALE_DAYS;
}

function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function useDebounced<T>(value: T, delayMs = 150): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return v;
}

function dateInputValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

// ============================================================
// Icons (inline SVG — no emoji anywhere)
// ============================================================

function Icon({ name, size = 16, color = 'currentColor', className }: {
  name: string;
  size?: number;
  color?: string;
  className?: string;
}) {
  const s = { width: size, height: size, flexShrink: 0 };
  const paths: Record<string, ReactElement> = {
    // Type icons
    bug: (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={s} className={className}>
        <path d="M8 2l1 2M16 2l-1 2M9 10V8a3 3 0 0 1 6 0v2"/>
        <path d="M12 10a6 6 0 0 1 6 6v2a6 6 0 0 1-12 0v-2a6 6 0 0 1 6-6z"/>
        <path d="M2 14h4M18 14h4M3 18l3-1M21 18l-3-1M3 10l3 1M21 10l-3 1"/>
      </svg>
    ),
    feature: (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={s} className={className}>
        <path d="M12 2l2.5 6.5L21 11l-6.5 2.5L12 20l-2.5-6.5L3 11l6.5-2.5z"/>
      </svg>
    ),
    chore: (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={s} className={className}>
        <circle cx="12" cy="12" r="3"/>
        <path d="M12 1v6M12 17v6M4.22 4.22l4.24 4.24M15.54 15.54l4.24 4.24M1 12h6M17 12h6M4.22 19.78l4.24-4.24M15.54 8.46l4.24-4.24"/>
      </svg>
    ),
    doc: (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={s} className={className}>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <path d="M14 2v6h6M9 13h6M9 17h4"/>
      </svg>
    ),
    // UI icons
    search: (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={s} className={className}>
        <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/>
      </svg>
    ),
    plus: (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={s} className={className}>
        <path d="M12 5v14M5 12h14"/>
      </svg>
    ),
    close: (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={s} className={className}>
        <path d="M18 6L6 18M6 6l12 12"/>
      </svg>
    ),
    overview: (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={s} className={className}>
        <rect x="3" y="3" width="7" height="7" rx="1"/>
        <rect x="14" y="3" width="7" height="7" rx="1"/>
        <rect x="3" y="14" width="7" height="7" rx="1"/>
        <rect x="14" y="14" width="7" height="7" rx="1"/>
      </svg>
    ),
    board: (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={s} className={className}>
        <rect x="3" y="3" width="4" height="18" rx="1"/>
        <rect x="10" y="3" width="4" height="12" rx="1"/>
        <rect x="17" y="3" width="4" height="7" rx="1"/>
      </svg>
    ),
    list: (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={s} className={className}>
        <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/>
      </svg>
    ),
    download: (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={s} className={className}>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
      </svg>
    ),
    calendar: (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={s} className={className}>
        <rect x="3" y="4" width="18" height="18" rx="2"/>
        <path d="M16 2v4M8 2v4M3 10h18"/>
      </svg>
    ),
    clock: (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={s} className={className}>
        <circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>
      </svg>
    ),
    tag: (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={s} className={className}>
        <path d="M20 12l-8 8-8-8 8-8h7a1 1 0 0 1 1 1z"/>
        <circle cx="14.5" cy="9.5" r="1" fill={color}/>
      </svg>
    ),
    user: (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={s} className={className}>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
    ),
    alert: (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={s} className={className}>
        <path d="M10.3 3.86l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.72-3.14l-8-14a2 2 0 0 0-3.42 0z"/>
        <path d="M12 9v4M12 17h.01"/>
      </svg>
    ),
    check: (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={s} className={className}>
        <path d="M20 6L9 17l-5-5"/>
      </svg>
    ),
    chevronRight: (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={s} className={className}>
        <path d="M9 18l6-6-6-6"/>
      </svg>
    ),
  };
  return paths[name] || null;
}

function TypeIcon({ type, size = 14 }: { type: TicketType; size?: number }) {
  return <Icon name={type} size={size} color={TYPE_META[type].color} />;
}

// ============================================================
// API wrapper
// ============================================================

async function apiFetch(path: string, token: string, init: RequestInit = {}): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'X-Project-Password': token,
      ...(init.headers || {}),
    },
  });
}

async function fetchTickets(token: string): Promise<Ticket[]> {
  const r = await apiFetch('/tickets', token);
  if (!r.ok) throw new Error(r.status === 401 ? 'Invalid password' : 'Failed to load tickets');
  return r.json();
}
async function createTicket(token: string, body: Partial<Ticket>): Promise<Ticket> {
  const r = await apiFetch('/tickets', token, { method: 'POST', body: JSON.stringify(body) });
  if (!r.ok) throw new Error('Failed to create ticket');
  return r.json();
}
async function patchTicket(token: string, id: string, body: Partial<Ticket>): Promise<Ticket> {
  const r = await apiFetch(`/tickets/${id}`, token, { method: 'PATCH', body: JSON.stringify(body) });
  if (!r.ok) throw new Error('Failed to update ticket');
  return r.json();
}
async function deleteTicket(token: string, id: string): Promise<void> {
  const r = await apiFetch(`/tickets/${id}`, token, { method: 'DELETE' });
  if (!r.ok) throw new Error('Failed to delete ticket');
}
async function addComment(token: string, id: string, text: string, author: string): Promise<Ticket> {
  const r = await apiFetch(`/tickets/${id}/comments`, token, {
    method: 'POST',
    body: JSON.stringify({ text, author }),
  });
  if (!r.ok) throw new Error('Failed to add comment');
  return r.json();
}
async function deleteComment(token: string, ticketId: string, index: number): Promise<Ticket> {
  const r = await apiFetch(`/tickets/${ticketId}/comments/${index}`, token, { method: 'DELETE' });
  if (!r.ok) throw new Error('Failed to delete comment');
  return r.json();
}

// ============================================================
// CSV export (client-side)
// ============================================================

function toCsv(tickets: Ticket[]): string {
  const esc = (v: any) => {
    const s = v == null ? '' : String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const headers = ['id', 'type', 'title', 'status', 'priority', 'assignee', 'dueDate', 'labels', 'commentsCount', 'createdAt', 'updatedAt'];
  const rows = tickets.map((t) => [
    t.id, t.type, t.title, t.status, t.priority, t.assignee,
    t.dueDate || '',
    (t.labels || []).join(';'),
    (t.comments || []).length,
    t.createdAt, t.updatedAt,
  ].map(esc).join(','));
  return [headers.join(','), ...rows].join('\n');
}

function downloadCsv(tickets: Ticket[]) {
  const csv = toCsv(tickets);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `treadai-tickets-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ============================================================
// Password gate
// ============================================================

function Gate({ onUnlock }: { onUnlock: (token: string) => void }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    setError('');
    try {
      await fetchTickets(password);
      sessionStorage.setItem(TOKEN_KEY, password);
      onUnlock(password);
    } catch (err: any) {
      setError(err?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2.5 justify-center mb-10">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-extrabold text-sm">T</div>
          <div className="text-slate-900 font-extrabold tracking-tight">TreadAI · Project</div>
        </div>
        <h1 className="text-2xl font-extrabold text-slate-900 text-center mb-2 tracking-tight">Enter the team password</h1>
        <p className="text-sm text-slate-500 text-center mb-8">Internal dashboard for TreadAI tickets and QA.</p>
        <form onSubmit={submit}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            placeholder="Password"
            className="w-full px-4 py-3 border border-slate-300 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/5"
          />
          {error && <div className="mt-3 text-sm text-red-600 font-medium">{error}</div>}
          <button
            type="submit"
            disabled={loading || !password}
            className="mt-4 w-full py-3 rounded-xl bg-slate-900 text-white font-semibold text-sm hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            {loading ? 'Checking…' : 'Enter'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// Ticket card (compact — used on board + my work)
// ============================================================

const TicketCard = memo(function TicketCard({ ticket, onOpen, onDragStart }: {
  ticket: Ticket;
  onOpen: () => void;
  onDragStart?: (e: React.DragEvent, ticket: Ticket) => void;
}) {
  const pri = PRIORITY_META[ticket.priority];
  const stale = isStale(ticket);
  const dueDays = daysUntil(ticket.dueDate);
  const dueBadge =
    dueDays === null ? null
    : dueDays < 0  ? { text: `${Math.abs(dueDays)}d overdue`, color: '#dc2626', bg: '#fef2f2' }
    : dueDays === 0 ? { text: 'Due today',                color: '#ea580c', bg: '#fff7ed' }
    : dueDays <= 3  ? { text: `Due in ${dueDays}d`,        color: '#ca8a04', bg: '#fefce8' }
    : null;

  return (
    <div
      draggable={!!onDragStart}
      onDragStart={(e) => onDragStart?.(e, ticket)}
      onClick={onOpen}
      className="group bg-white border border-slate-200 rounded-lg px-3 py-3 cursor-pointer hover:border-slate-400 hover:shadow-sm transition-all"
    >
      <div className="flex items-start gap-2 mb-2">
        <TypeIcon type={ticket.type} />
        <div className="text-[11px] font-mono text-slate-400 tracking-tight shrink-0 pt-0.5">{ticket.id}</div>
        {stale && (
          <div className="ml-auto flex items-center gap-1 text-[10px] font-semibold text-slate-400" title={`No update in ${STALE_DAYS}+ days`}>
            <Icon name="clock" size={11} />
            <span>stale</span>
          </div>
        )}
      </div>
      <div className="text-[13px] font-semibold text-slate-900 leading-snug mb-3 line-clamp-3">
        {ticket.title}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className="text-[10px] font-extrabold px-1.5 py-0.5 rounded"
          style={{ color: pri.color, backgroundColor: pri.soft }}
        >
          {pri.label}
        </span>
        {dueBadge && (
          <span
            className="text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1"
            style={{ color: dueBadge.color, backgroundColor: dueBadge.bg }}
          >
            <Icon name="calendar" size={10} color={dueBadge.color} />
            {dueBadge.text}
          </span>
        )}
        {ticket.labels.slice(0, 2).map((l) => (
          <span key={l} className="text-[10px] font-semibold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">
            {l}
          </span>
        ))}
        <div className="ml-auto">
          <div
            className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-700"
            title={ticket.assignee}
          >
            {ticket.assignee.slice(0, 1).toUpperCase()}
          </div>
        </div>
      </div>
    </div>
  );
});

// ============================================================
// Ticket drawer (full detail)
// ============================================================

function TicketDrawer({
  ticket, token, author, onClose, onUpdate, onDelete,
}: {
  ticket: Ticket;
  token: string;
  author: string;
  onClose: () => void;
  onUpdate: (t: Ticket) => void;
  onDelete: (id: string) => void;
}) {
  const [draftTitle, setDraftTitle] = useState(ticket.title);
  const [editingTitle, setEditingTitle] = useState(false);
  const [draftDesc, setDraftDesc] = useState(ticket.description);
  const [editingDesc, setEditingDesc] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const commentRef = useRef<HTMLTextAreaElement | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setDraftTitle(ticket.title);
    setDraftDesc(ticket.description);
  }, [ticket.id]);

  const saveField = async (field: keyof Ticket, value: any) => {
    setSaveState('saving');
    try {
      const updated = await patchTicket(token, ticket.id, { [field]: value });
      onUpdate(updated);
      setSaveState('saved');
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaveState('idle'), 1400);
    } catch {
      setSaveState('idle');
    }
  };

  useEffect(() => () => {
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
  }, []);

  const addLabel = async () => {
    const v = newLabel.trim();
    if (!v) return;
    if (ticket.labels.includes(v)) { setNewLabel(''); return; }
    const next = [...ticket.labels, v];
    setNewLabel('');
    await saveField('labels' as keyof Ticket, next);
  };

  const removeLabel = async (l: string) => {
    const next = ticket.labels.filter((x) => x !== l);
    await saveField('labels' as keyof Ticket, next);
  };

  const submitComment = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const text = commentDraft.trim();
    if (!text) return;
    setPosting(true);
    try {
      const updated = await addComment(token, ticket.id, text, author);
      onUpdate(updated);
      setCommentDraft('');
      commentRef.current?.focus();
    } finally {
      setPosting(false);
    }
  };

  // ESC closes · Cmd+Enter submits comment · 1-4 moves status (when not typing)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && commentDraft.trim()) submitComment();
      const target = e.target as HTMLElement;
      const typing = target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      );
      if (!typing && ['1', '2', '3', '4'].includes(e.key)) {
        const map: Record<string, TicketStatus> = { '1': 'backlog', '2': 'in_progress', '3': 'qa', '4': 'done' };
        const next = map[e.key];
        if (next && next !== ticket.status) {
          e.preventDefault();
          saveField('status', next);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [commentDraft, ticket.status]);

  const pri = PRIORITY_META[ticket.priority];
  const type = TYPE_META[ticket.type];
  const dueDays = daysUntil(ticket.dueDate);
  const stale = isStale(ticket);

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-slate-900/30 backdrop-blur-[2px]" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl border-l border-slate-200"
      >
        {/* Top bar */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <TypeIcon type={ticket.type} />
            <span className="font-mono text-slate-400 text-xs">{ticket.id}</span>
            <span className="text-slate-300">·</span>
            <span className="text-slate-500 text-xs">{type.label}</span>
            {saveState !== 'idle' && (
              <span className={`ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded transition-opacity ${
                saveState === 'saving' ? 'text-slate-400 bg-slate-100' : 'text-emerald-700 bg-emerald-50'
              }`}>
                {saveState === 'saving' ? 'Saving…' : 'Saved'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {author !== ticket.assignee && (
              <button
                onClick={() => saveField('assignee', author)}
                className="text-xs text-slate-500 hover:text-slate-900 font-semibold px-2 py-1 rounded hover:bg-slate-100"
                title="Assign this ticket to me"
              >
                Assign to me
              </button>
            )}
            <button
              onClick={() => setConfirmDelete(true)}
              className="text-slate-400 hover:text-red-600 text-xs font-semibold px-2 py-1 rounded hover:bg-slate-100"
              title="Delete ticket"
            >
              Delete
            </button>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-900 w-6 h-6 flex items-center justify-center rounded hover:bg-slate-100"
              title="Close (ESC)"
            >
              <Icon name="close" size={16} />
            </button>
          </div>
        </div>

        <div className="px-6 py-5">
          {/* Title — click to edit */}
          {editingTitle ? (
            <input
              value={draftTitle}
              autoFocus
              onChange={(e) => setDraftTitle(e.target.value)}
              onBlur={() => {
                setEditingTitle(false);
                if (draftTitle.trim() && draftTitle !== ticket.title) saveField('title', draftTitle.trim());
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                if (e.key === 'Escape') { setDraftTitle(ticket.title); setEditingTitle(false); }
              }}
              className="w-full text-xl font-extrabold text-slate-900 tracking-tight bg-slate-50 rounded-lg px-3 py-2 border border-slate-300 focus:outline-none focus:border-slate-900"
            />
          ) : (
            <h1
              onClick={() => setEditingTitle(true)}
              className="text-xl font-extrabold text-slate-900 tracking-tight cursor-text hover:bg-slate-50 rounded-lg px-3 py-2 -mx-3"
            >
              {ticket.title}
            </h1>
          )}

          {stale && (
            <div className="mt-3 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              <Icon name="clock" size={14} color="#b45309" />
              <span className="font-semibold">This ticket hasn't been updated in {STALE_DAYS}+ days.</span>
            </div>
          )}

          <div className="mt-3 text-[10px] text-slate-400 font-medium">
            Quick move: <kbd className="font-mono bg-slate-100 px-1 rounded">1</kbd> Backlog
            {' · '}<kbd className="font-mono bg-slate-100 px-1 rounded">2</kbd> In Progress
            {' · '}<kbd className="font-mono bg-slate-100 px-1 rounded">3</kbd> QA
            {' · '}<kbd className="font-mono bg-slate-100 px-1 rounded">4</kbd> Done
          </div>

          {/* Status chips — one-click status change */}
          <div className="mt-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Status</div>
            <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
              {STATUS_ORDER.map((s) => {
                const active = ticket.status === s;
                const meta = STATUS_META[s];
                return (
                  <button
                    key={s}
                    onClick={() => s !== ticket.status && saveField('status', s)}
                    className={`flex-1 text-xs font-semibold px-2 py-1.5 rounded-md transition-all flex items-center justify-center gap-1.5 ${
                      active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                    }`}
                    title={`Press ${STATUS_ORDER.indexOf(s) + 1} to move here`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: meta.dot }} />
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Meta grid — priority / type / assignee / due */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 mt-4 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Priority</span>
              <select
                value={ticket.priority}
                onChange={(e) => saveField('priority', e.target.value as TicketPriority)}
                className="font-semibold bg-transparent border border-slate-200 rounded-md px-2 py-1 text-xs"
                style={{ color: pri.color }}
              >
                {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Type</span>
              <select
                value={ticket.type}
                onChange={(e) => saveField('type', e.target.value as TicketType)}
                className="font-semibold text-slate-900 bg-transparent border border-slate-200 rounded-md px-2 py-1 text-xs"
              >
                {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Assignee</span>
              <input
                value={ticket.assignee}
                onChange={(e) => onUpdate({ ...ticket, assignee: e.target.value })}
                onBlur={(e) => saveField('assignee', e.target.value.trim() || 'Unassigned')}
                className="font-semibold text-slate-900 bg-transparent border border-slate-200 rounded-md px-2 py-1 text-xs w-28 text-right"
              />
            </div>
            <div className="flex items-center justify-between col-span-2">
              <span className="text-slate-500 text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5">
                <Icon name="calendar" size={12} color="#64748b" />
                Due date
              </span>
              <div className="flex items-center gap-2">
                {ticket.dueDate && dueDays !== null && (
                  <span className={`text-xs font-semibold ${dueDays < 0 ? 'text-red-600' : dueDays <= 3 ? 'text-amber-600' : 'text-slate-500'}`}>
                    {dueDays < 0 ? `${Math.abs(dueDays)}d overdue` : dueDays === 0 ? 'Today' : `in ${dueDays}d`}
                  </span>
                )}
                <input
                  type="date"
                  value={dateInputValue(ticket.dueDate)}
                  onChange={(e) => saveField('dueDate', e.target.value || null)}
                  className="font-semibold text-slate-900 bg-transparent border border-slate-200 rounded-md px-2 py-1 text-xs"
                />
              </div>
            </div>
          </div>

          <div className="text-xs text-slate-400 mt-4 border-t border-slate-100 pt-3">
            Created {timeAgo(ticket.createdAt)} · Updated {timeAgo(ticket.updatedAt)}
          </div>

          {/* Labels */}
          <div className="mt-6">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
              <Icon name="tag" size={12} color="#64748b" />
              Labels
            </div>
            <div className="flex items-center flex-wrap gap-1.5">
              {ticket.labels.map((l) => (
                <span
                  key={l}
                  className="group/label text-[11px] font-semibold text-slate-700 bg-slate-100 rounded-md pl-2 pr-1 py-0.5 flex items-center gap-1"
                >
                  {l}
                  <button
                    onClick={() => removeLabel(l)}
                    className="w-4 h-4 flex items-center justify-center text-slate-400 hover:text-slate-900 hover:bg-slate-200 rounded"
                    title={`Remove ${l}`}
                  >
                    <Icon name="close" size={10} />
                  </button>
                </span>
              ))}
              <input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addLabel(); } }}
                placeholder="Add label…"
                className="text-[11px] bg-transparent border border-dashed border-slate-300 rounded-md px-2 py-0.5 w-28 focus:outline-none focus:border-slate-900"
              />
            </div>
          </div>

          {/* Description — click to edit, blur to save, renders markdown when not editing */}
          <div className="mt-6">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center justify-between">
              <span>Description</span>
              <span className="text-[10px] font-medium text-slate-400 normal-case tracking-normal">
                Markdown: **bold** · *italic* · `code` · [link](url) · - lists · # headers
              </span>
            </div>
            {editingDesc ? (
              <textarea
                autoFocus
                value={draftDesc}
                onChange={(e) => setDraftDesc(e.target.value)}
                onBlur={() => {
                  setEditingDesc(false);
                  if (draftDesc !== ticket.description) saveField('description', draftDesc);
                }}
                placeholder="Add a description…"
                rows={8}
                className="w-full text-sm text-slate-700 bg-white rounded-lg px-3 py-2 border border-slate-900 focus:outline-none leading-relaxed resize-y font-mono"
              />
            ) : ticket.description ? (
              <div
                onClick={() => setEditingDesc(true)}
                className="text-sm text-slate-700 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200 hover:border-slate-400 cursor-text leading-relaxed [&_a]:text-blue-600 [&_a]:hover:underline"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(ticket.description) }}
              />
            ) : (
              <div
                onClick={() => setEditingDesc(true)}
                className="text-sm text-slate-400 italic bg-slate-50 rounded-lg px-3 py-2 border border-dashed border-slate-200 hover:border-slate-400 cursor-text"
              >
                Add a description…
              </div>
            )}
          </div>

          {/* Activity */}
          <div className="mt-8">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
              Activity {ticket.comments.length > 0 && <span className="text-slate-400 ml-1">({ticket.comments.length})</span>}
            </div>

            {ticket.comments.length === 0 ? (
              <div className="text-sm text-slate-400 italic mb-4">No activity yet. Start the conversation.</div>
            ) : (
              <div className="space-y-4 mb-4">
                {ticket.comments.map((c, i) => (
                  <div key={i} className="flex gap-3 group/comment">
                    <div className="w-7 h-7 rounded-full bg-slate-200 shrink-0 flex items-center justify-center text-[11px] font-bold text-slate-700">
                      {c.author.slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="font-semibold text-slate-900 text-sm">{c.author}</span>
                        <span className="text-xs text-slate-400">{timeAgo(c.createdAt)}</span>
                        {(c.author === author || author === 'Majid') && (
                          <button
                            onClick={async () => {
                              const updated = await deleteComment(token, ticket.id, i);
                              onUpdate(updated);
                            }}
                            className="ml-auto opacity-0 group-hover/comment:opacity-100 text-xs text-slate-400 hover:text-red-600 transition-opacity"
                            title="Delete comment"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                      <div
                        className="text-sm text-slate-700 mt-1 [&_a]:text-blue-600 [&_a]:hover:underline"
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(c.text) }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            <form onSubmit={submitComment} className="flex gap-3">
              <div className="w-7 h-7 rounded-full bg-slate-900 shrink-0 flex items-center justify-center text-[11px] font-bold text-white">
                {author.slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1">
                <textarea
                  ref={commentRef}
                  value={commentDraft}
                  onChange={(e) => setCommentDraft(e.target.value)}
                  placeholder="Add a comment… (⌘ Enter to send)"
                  rows={2}
                  className="w-full text-sm text-slate-900 bg-white rounded-lg px-3 py-2 border border-slate-300 focus:outline-none focus:border-slate-900 resize-none"
                />
                <div className="flex justify-end mt-2">
                  <button
                    type="submit"
                    disabled={posting || !commentDraft.trim()}
                    className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-40"
                  >
                    {posting ? 'Posting…' : 'Comment'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Delete confirm */}
        {confirmDelete && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50" onClick={() => setConfirmDelete(false)}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-6 max-w-sm w-[90%] shadow-xl">
              <h3 className="text-base font-extrabold text-slate-900">Delete {ticket.id}?</h3>
              <p className="text-sm text-slate-500 mt-1">This can't be undone.</p>
              <div className="mt-5 flex gap-2 justify-end">
                <button onClick={() => setConfirmDelete(false)} className="px-3 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900">Cancel</button>
                <button
                  onClick={async () => {
                    await deleteTicket(token, ticket.id);
                    onDelete(ticket.id);
                    setConfirmDelete(false);
                    onClose();
                  }}
                  className="px-3 py-2 text-sm font-semibold rounded-md bg-red-600 text-white hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// New ticket modal
// ============================================================

function NewTicketModal({
  defaultStatus, defaultAssignee, onClose, onCreate,
}: {
  defaultStatus: TicketStatus;
  defaultAssignee: string;
  onClose: () => void;
  onCreate: (payload: Partial<Ticket>) => Promise<void>;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<TicketType>(() => {
    const v = localStorage.getItem(LAST_TYPE_KEY);
    return (v === 'bug' || v === 'feature' || v === 'chore' || v === 'doc') ? v : 'feature';
  });
  const [priority, setPriority] = useState<TicketPriority>(() => {
    const v = localStorage.getItem(LAST_PRIORITY_KEY);
    return (v === 'p0' || v === 'p1' || v === 'p2' || v === 'p3') ? v : 'p2';
  });
  const [status, setStatus] = useState<TicketStatus>(defaultStatus);
  const [assignee, setAssignee] = useState(defaultAssignee);
  const [dueDate, setDueDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setStatus(defaultStatus); }, [defaultStatus]);
  useEffect(() => { setAssignee(defaultAssignee); }, [defaultAssignee]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      await onCreate({
        title: title.trim(), description, type, priority, status, assignee,
        dueDate: dueDate || null,
      });
      // Remember user's choices so next ticket defaults to them
      localStorage.setItem(LAST_TYPE_KEY, type);
      localStorage.setItem(LAST_PRIORITY_KEY, priority);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm px-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-lg shadow-2xl border border-slate-200"
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-extrabold text-slate-900 tracking-tight">New ticket</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-900 w-6 h-6 flex items-center justify-center">
            <Icon name="close" size={16} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title"
            className="w-full text-base font-semibold text-slate-900 placeholder:text-slate-400 bg-transparent border-b border-slate-200 focus:outline-none focus:border-slate-900 py-2"
          />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description (optional)"
            rows={4}
            className="w-full text-sm text-slate-700 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200 focus:outline-none focus:border-slate-400 focus:bg-white resize-y"
          />
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Type
              <div className="mt-1 flex items-center gap-2 border border-slate-200 rounded-md px-2 py-2">
                <TypeIcon type={type} size={16} />
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as TicketType)}
                  className="flex-1 text-sm text-slate-900 bg-transparent focus:outline-none"
                >
                  {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            </label>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Priority
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as TicketPriority)}
                className="mt-1 w-full text-sm border border-slate-200 rounded-md px-2 py-2 focus:outline-none focus:border-slate-900 font-semibold"
                style={{ color: PRIORITY_META[priority].color }}
              >
                {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </label>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Status
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as TicketStatus)}
                className="mt-1 w-full text-sm text-slate-900 border border-slate-200 rounded-md px-2 py-2 focus:outline-none focus:border-slate-900"
              >
                {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
              </select>
            </label>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Assignee
              <input
                value={assignee}
                onChange={(e) => setAssignee(e.target.value)}
                className="mt-1 w-full text-sm text-slate-900 border border-slate-200 rounded-md px-2 py-2 focus:outline-none focus:border-slate-900"
              />
            </label>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 col-span-2">
              Due date (optional)
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="mt-1 w-full text-sm text-slate-900 border border-slate-200 rounded-md px-2 py-2 focus:outline-none focus:border-slate-900"
              />
            </label>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
          <span className="text-xs text-slate-400">ESC to cancel</span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="px-3 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900">Cancel</button>
            <button
              type="submit"
              disabled={!title.trim() || submitting}
              className="px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-40"
            >
              {submitting ? 'Creating…' : 'Create ticket'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

// ============================================================
// Overview view
// ============================================================

function Overview({ tickets, author, onOpenTicket, onSwitchToBoard }: {
  tickets: Ticket[];
  author: string;
  onOpenTicket: (t: Ticket) => void;
  onSwitchToBoard: () => void;
}) {
  const counts = useMemo(() => {
    const c = { backlog: 0, in_progress: 0, qa: 0, done7d: 0, blocked: 0 };
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    for (const t of tickets) {
      if (t.status === 'backlog') c.backlog++;
      else if (t.status === 'in_progress') c.in_progress++;
      else if (t.status === 'qa') c.qa++;
      else if (t.status === 'done' && new Date(t.updatedAt).getTime() > sevenDaysAgo) c.done7d++;
      if (t.priority === 'p0' && t.status !== 'done') c.blocked++;
    }
    return c;
  }, [tickets]);

  const myWork = useMemo(() => {
    return tickets
      .filter((t) => t.assignee === author && t.status !== 'done')
      .sort((a, b) => {
        const PR: Record<TicketPriority, number> = { p0: 0, p1: 1, p2: 2, p3: 3 };
        return PR[a.priority] - PR[b.priority] || a.updatedAt.localeCompare(b.updatedAt);
      })
      .slice(0, 6);
  }, [tickets, author]);

  const attention = useMemo(() => {
    return tickets
      .filter((t) => (t.priority === 'p0' || t.priority === 'p1') && t.status !== 'done')
      .sort((a, b) => a.priority.localeCompare(b.priority))
      .slice(0, 5);
  }, [tickets]);

  const activity = useMemo(() => {
    return [...tickets]
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
      .slice(0, 8);
  }, [tickets]);

  const StatCard = ({ value, label, color }: { value: number; label: string; color: string }) => (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
      <div className="text-2xl font-extrabold tracking-tight" style={{ color }}>{value}</div>
      <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );

  return (
    <div className="max-w-[1500px] mx-auto px-6 py-6">
      {/* Count strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        <StatCard value={counts.backlog}     label="Backlog"       color="#334155" />
        <StatCard value={counts.in_progress} label="In Progress"   color="#2563eb" />
        <StatCard value={counts.qa}          label="QA"            color="#7c3aed" />
        <StatCard value={counts.blocked}     label="P0 Blockers"   color="#dc2626" />
        <StatCard value={counts.done7d}      label="Done last 7d"  color="#059669" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* My Work */}
        <section className="lg:col-span-1 bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-extrabold uppercase tracking-wider text-slate-900">My Work</h2>
            <span className="text-xs text-slate-400 font-semibold">{myWork.length}</span>
          </div>
          {myWork.length === 0 ? (
            <div className="text-sm text-slate-400 py-6 text-center">
              Nothing assigned to <b className="text-slate-600">{author}</b>.
              <div className="mt-1 text-xs text-slate-400">Enjoy the quiet.</div>
            </div>
          ) : (
            <div className="space-y-2">
              {myWork.map((t) => (
                <OverviewRow key={t.id} ticket={t} onOpen={() => onOpenTicket(t)} />
              ))}
            </div>
          )}
        </section>

        {/* Needs attention */}
        <section className="lg:col-span-1 bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-extrabold uppercase tracking-wider text-slate-900 flex items-center gap-1.5">
              <Icon name="alert" size={14} color="#dc2626" />
              Needs Attention
            </h2>
            <span className="text-xs text-slate-400 font-semibold">{attention.length}</span>
          </div>
          {attention.length === 0 ? (
            <div className="text-sm text-slate-400 py-6 text-center">
              No P0 or P1 tickets open. All clear.
            </div>
          ) : (
            <div className="space-y-2">
              {attention.map((t) => (
                <OverviewRow key={t.id} ticket={t} onOpen={() => onOpenTicket(t)} showStatus />
              ))}
            </div>
          )}
        </section>

        {/* Activity */}
        <section className="lg:col-span-1 bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-extrabold uppercase tracking-wider text-slate-900">Recent Activity</h2>
            <span className="text-xs text-slate-400 font-semibold">{activity.length}</span>
          </div>
          {activity.length === 0 ? (
            <div className="text-sm text-slate-400 py-6 text-center">No activity yet.</div>
          ) : (
            <div className="space-y-3">
              {activity.map((t) => (
                <button
                  key={t.id}
                  onClick={() => onOpenTicket(t)}
                  className="w-full text-left group"
                >
                  <div className="flex items-start gap-2.5">
                    <TypeIcon type={t.type} size={13} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-1.5">
                        <span className="font-mono text-[10px] text-slate-400">{t.id}</span>
                        <span className="text-[10px] text-slate-400">·</span>
                        <span className="text-[10px] text-slate-500 capitalize">{STATUS_META[t.status].label}</span>
                      </div>
                      <div className="text-xs font-semibold text-slate-900 group-hover:text-blue-600 truncate">
                        {t.title}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        {t.assignee} · updated {timeAgo(t.updatedAt)}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>

      <div className="mt-10 text-center">
        <button
          onClick={onSwitchToBoard}
          className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600 hover:text-slate-900"
        >
          Open the full board
          <Icon name="chevronRight" size={14} />
        </button>
      </div>
    </div>
  );
}

function OverviewRow({ ticket, onOpen, showStatus }: { ticket: Ticket; onOpen: () => void; showStatus?: boolean }) {
  const pri = PRIORITY_META[ticket.priority];
  return (
    <button
      onClick={onOpen}
      className="w-full text-left flex items-start gap-2.5 px-2.5 py-2 rounded-md hover:bg-slate-50 transition-colors"
    >
      <TypeIcon type={ticket.type} />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-slate-900 truncate">{ticket.title}</div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className="font-mono text-[10px] text-slate-400">{ticket.id}</span>
          <span
            className="text-[10px] font-extrabold px-1.5 rounded"
            style={{ color: pri.color, backgroundColor: pri.soft }}
          >
            {pri.label}
          </span>
          {showStatus && (
            <span className="text-[10px] text-slate-500 capitalize">
              · {STATUS_META[ticket.status].label}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

// ============================================================
// Board view
// ============================================================

function Board({
  tickets, filteredTickets, onOpenTicket, onDragStart, onColumnDrop, onNewInStatus,
}: {
  tickets: Ticket[];
  filteredTickets: Ticket[];
  onOpenTicket: (t: Ticket) => void;
  onDragStart: (e: React.DragEvent, t: Ticket) => void;
  onColumnDrop: (e: React.DragEvent, status: TicketStatus) => void;
  onNewInStatus: (status: TicketStatus) => void;
}) {
  const columns = useMemo(() => {
    const grouped: Record<TicketStatus, Ticket[]> = { backlog: [], in_progress: [], qa: [], done: [] };
    filteredTickets.forEach((t) => grouped[t.status].push(t));
    const PR_RANK: Record<TicketPriority, number> = { p0: 0, p1: 1, p2: 2, p3: 3 };
    Object.values(grouped).forEach((list) =>
      list.sort((a, b) => PR_RANK[a.priority] - PR_RANK[b.priority] || b.updatedAt.localeCompare(a.updatedAt))
    );
    return grouped;
  }, [filteredTickets]);

  const allowDrop = (e: React.DragEvent) => e.preventDefault();
  const totalCount = tickets.length;
  const filteredCount = filteredTickets.length;
  const filtered = totalCount !== filteredCount;

  return (
    <div className="max-w-[1500px] mx-auto px-4 py-6">
      {filtered && (
        <div className="mb-4 text-xs text-slate-500">
          Showing <b className="text-slate-900">{filteredCount}</b> of <b className="text-slate-900">{totalCount}</b> tickets
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {STATUS_ORDER.map((status) => {
          const meta = STATUS_META[status];
          const cards = columns[status];
          return (
            <div
              key={status}
              onDragOver={allowDrop}
              onDrop={(e) => onColumnDrop(e, status)}
              className="bg-slate-50 rounded-xl p-3 min-h-[300px]"
            >
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: meta.dot }} />
                  <span className="text-xs font-extrabold uppercase tracking-wider text-slate-700">{meta.label}</span>
                  <span className="text-xs font-semibold text-slate-400 bg-white rounded-full px-2 py-0.5">{cards.length}</span>
                </div>
                <button
                  onClick={() => onNewInStatus(status)}
                  className="text-slate-400 hover:text-slate-900 w-6 h-6 flex items-center justify-center rounded hover:bg-white"
                  title="Add to this column"
                >
                  <Icon name="plus" size={14} />
                </button>
              </div>
              <div className="space-y-2">
                {cards.map((t) => (
                  <TicketCard key={t.id} ticket={t} onOpen={() => onOpenTicket(t)} onDragStart={onDragStart} />
                ))}
                {cards.length === 0 && (
                  <button
                    onClick={() => onNewInStatus(status)}
                    className="w-full text-xs text-slate-400 hover:text-slate-700 italic px-1 py-4 border border-dashed border-transparent hover:border-slate-300 rounded-md transition-colors"
                  >
                    Nothing here. Click to add.
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// List view (sortable table)
// ============================================================

type SortKey = 'id' | 'title' | 'status' | 'priority' | 'assignee' | 'dueDate' | 'updatedAt';
type SortDir = 'asc' | 'desc';

function ListView({ tickets, onOpenTicket }: {
  tickets: Ticket[];
  onOpenTicket: (t: Ticket) => void;
}) {
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: 'priority', dir: 'asc' });

  const sorted = useMemo(() => {
    const PR: Record<TicketPriority, number> = { p0: 0, p1: 1, p2: 2, p3: 3 };
    const ST: Record<TicketStatus, number> = { backlog: 0, in_progress: 1, qa: 2, done: 3 };
    const copy = [...tickets];
    copy.sort((a, b) => {
      let va: any, vb: any;
      if (sort.key === 'priority') { va = PR[a.priority]; vb = PR[b.priority]; }
      else if (sort.key === 'status') { va = ST[a.status]; vb = ST[b.status]; }
      else if (sort.key === 'dueDate') {
        va = a.dueDate ? new Date(a.dueDate).getTime() : Number.POSITIVE_INFINITY;
        vb = b.dueDate ? new Date(b.dueDate).getTime() : Number.POSITIVE_INFINITY;
      } else if (sort.key === 'updatedAt') {
        va = new Date(a.updatedAt).getTime(); vb = new Date(b.updatedAt).getTime();
      } else {
        va = (a as any)[sort.key] || ''; vb = (b as any)[sort.key] || '';
        if (typeof va === 'string') { va = va.toLowerCase(); vb = (vb as string).toLowerCase(); }
      }
      if (va < vb) return sort.dir === 'asc' ? -1 : 1;
      if (va > vb) return sort.dir === 'asc' ? 1 : -1;
      return b.updatedAt.localeCompare(a.updatedAt);
    });
    return copy;
  }, [tickets, sort]);

  const toggleSort = (key: SortKey) => {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    );
  };

  const Th = ({ k, label, className = '' }: { k: SortKey; label: string; className?: string }) => (
    <th
      onClick={() => toggleSort(k)}
      className={`px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 cursor-pointer hover:text-slate-900 select-none ${className}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sort.key === k && (
          <span className="text-slate-900">{sort.dir === 'asc' ? '↑' : '↓'}</span>
        )}
      </span>
    </th>
  );

  return (
    <div className="max-w-[1500px] mx-auto px-6 py-6">
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <Th k="id" label="ID" />
                <Th k="title" label="Title" />
                <Th k="status" label="Status" />
                <Th k="priority" label="Priority" />
                <Th k="assignee" label="Assignee" />
                <Th k="dueDate" label="Due" />
                <Th k="updatedAt" label="Updated" className="text-right" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((t) => {
                const pri = PRIORITY_META[t.priority];
                const dueDays = daysUntil(t.dueDate);
                return (
                  <tr
                    key={t.id}
                    onClick={() => onOpenTicket(t)}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                  >
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <TypeIcon type={t.type} size={13} />
                        <span className="font-mono text-xs text-slate-500">{t.id}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-slate-900 truncate max-w-[400px]">{t.title}</div>
                      {t.labels.length > 0 && (
                        <div className="flex gap-1 mt-0.5 flex-wrap">
                          {t.labels.map((l) => (
                            <span key={l} className="text-[9px] font-semibold text-slate-600 bg-slate-100 px-1 rounded">{l}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: STATUS_META[t.status].dot }} />
                        <span className="text-xs">{STATUS_META[t.status].label}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span
                        className="text-[10px] font-extrabold px-1.5 py-0.5 rounded"
                        style={{ color: pri.color, backgroundColor: pri.soft }}
                      >
                        {pri.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-[9px] font-bold text-slate-700">
                          {t.assignee.slice(0, 1).toUpperCase()}
                        </div>
                        <span className="text-xs text-slate-700">{t.assignee}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs">
                      {t.dueDate && dueDays !== null ? (
                        <span className={dueDays < 0 ? 'text-red-600 font-semibold' : dueDays <= 3 ? 'text-amber-600 font-semibold' : 'text-slate-500'}>
                          {dueDays < 0 ? `${Math.abs(dueDays)}d overdue` : dueDays === 0 ? 'Today' : `in ${dueDays}d`}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-500 text-right">
                      {timeAgo(t.updatedAt)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {sorted.length === 0 && (
            <div className="text-center py-16 text-sm text-slate-400">No tickets match the current filters.</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// ⌘K quick switcher
// ============================================================

function QuickSwitcher({ tickets, onOpen, onClose }: {
  tickets: Ticket[];
  onOpen: (t: Ticket) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = [...tickets].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    if (!q) return list.slice(0, 8);
    return list.filter((t) => {
      const hay = `${t.id} ${t.title} ${t.assignee} ${(t.labels || []).join(' ')}`.toLowerCase();
      return hay.includes(q);
    }).slice(0, 10);
  }, [tickets, query]);

  useEffect(() => { setIndex(0); }, [query]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowDown') { e.preventDefault(); setIndex((i) => Math.min(i + 1, matches.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setIndex((i) => Math.max(i - 1, 0)); }
      if (e.key === 'Enter' && matches[index]) {
        e.preventDefault();
        onOpen(matches[index]);
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [matches, index]);

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/40 backdrop-blur-sm px-4 pt-20" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-w-xl mx-auto bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
          <Icon name="search" size={16} color="#64748b" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Jump to a ticket by id, title, assignee…"
            className="flex-1 text-sm text-slate-900 placeholder:text-slate-400 bg-transparent focus:outline-none"
          />
          <kbd className="font-mono text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">ESC</kbd>
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          {matches.length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-400">No matches.</div>
          ) : (
            matches.map((t, i) => (
              <button
                key={t.id}
                onMouseEnter={() => setIndex(i)}
                onClick={() => { onOpen(t); onClose(); }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left ${
                  i === index ? 'bg-slate-100' : 'hover:bg-slate-50'
                }`}
              >
                <TypeIcon type={t.type} size={14} />
                <span className="font-mono text-xs text-slate-400 shrink-0 w-16">{t.id}</span>
                <span className="flex-1 text-sm font-semibold text-slate-900 truncate">{t.title}</span>
                <span className="text-xs text-slate-500 shrink-0">{t.assignee}</span>
              </button>
            ))
          )}
        </div>
        <div className="px-4 py-2 border-t border-slate-100 text-[10px] text-slate-400 flex items-center gap-3">
          <span><kbd className="font-mono bg-slate-100 px-1 rounded">↑↓</kbd> navigate</span>
          <span><kbd className="font-mono bg-slate-100 px-1 rounded">Enter</kbd> open</span>
          <span className="ml-auto">{matches.length} result{matches.length === 1 ? '' : 's'}</span>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Dashboard shell
// ============================================================

function Dashboard({ token }: { token: string }) {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [openTicket, setOpenTicket] = useState<Ticket | null>(null);
  const [newModalStatus, setNewModalStatus] = useState<TicketStatus | null>(null);
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [filterPriority, setFilterPriority] = useState<'all' | TicketPriority>('all');
  const [onlyMine, setOnlyMine] = useState(false);
  const [search, setSearch] = useState('');
  // Debounced copy — avoids re-filtering on every keystroke on larger lists
  const debouncedSearch = useDebounced(search, 140);
  const [view, setView] = useState<View>(() => (sessionStorage.getItem(VIEW_KEY) as View) || 'overview');
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const dragId = useRef<string | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const [author, setAuthor] = useState(() => localStorage.getItem(AUTHOR_KEY) || 'Majid');
  const [editingAuthor, setEditingAuthor] = useState(false);

  useEffect(() => { sessionStorage.setItem(VIEW_KEY, view); }, [view]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      setTickets(await fetchTickets(token));
    } catch (e: any) {
      setErr(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const assignees = useMemo(() => {
    const set = new Set<string>();
    tickets.forEach((t) => t.assignee && set.add(t.assignee));
    return Array.from(set).sort();
  }, [tickets]);

  const visible = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return tickets.filter((t) => {
      if (onlyMine && t.assignee !== author) return false;
      if (filterAssignee !== 'all' && t.assignee !== filterAssignee) return false;
      if (filterPriority !== 'all' && t.priority !== filterPriority) return false;
      if (q) {
        const hay = `${t.title} ${t.id} ${(t.labels || []).join(' ')} ${t.assignee}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tickets, filterAssignee, filterPriority, debouncedSearch, onlyMine, author]);

  // CRUD helpers used by children
  const handleCreate = async (payload: Partial<Ticket>) => {
    const created = await createTicket(token, payload);
    setTickets((prev) => [...prev, created]);
  };
  const handleUpdate = (t: Ticket) => {
    setTickets((prev) => prev.map((p) => (p.id === t.id ? t : p)));
    setOpenTicket((cur) => (cur?.id === t.id ? t : cur));
  };
  const handleDelete = (id: string) => {
    setTickets((prev) => prev.filter((t) => t.id !== id));
  };

  // Drag + drop
  const onDragStart = (e: React.DragEvent, t: Ticket) => {
    dragId.current = t.id;
    e.dataTransfer.effectAllowed = 'move';
  };
  const onColumnDrop = async (e: React.DragEvent, newStatus: TicketStatus) => {
    e.preventDefault();
    const id = dragId.current;
    dragId.current = null;
    if (!id) return;
    const original = tickets.find((t) => t.id === id);
    if (!original || original.status === newStatus) return;
    setTickets((prev) => prev.map((t) => (t.id === id ? { ...t, status: newStatus } : t)));
    try {
      const updated = await patchTicket(token, id, { status: newStatus });
      setTickets((prev) => prev.map((t) => (t.id === id ? updated : t)));
    } catch {
      setTickets((prev) => prev.map((t) => (t.id === id ? original : t)));
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const typing = target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      );
      if (typing) return;
      if (openTicket || newModalStatus) return;

      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSwitcherOpen(true);
      } else if (e.key === 'c' || e.key === 'C') {
        e.preventDefault();
        setNewModalStatus('backlog');
      } else if (e.key === '/') {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === 'g') {
        // Chord: g then o/b/l for view switch
        const handler = (e2: KeyboardEvent) => {
          if (e2.key === 'o') setView('overview');
          if (e2.key === 'b') setView('board');
          if (e2.key === 'l') setView('list');
          window.removeEventListener('keydown', handler, true);
        };
        window.addEventListener('keydown', handler, { capture: true, once: true });
      }
    };
    // Cmd+K always active, even when typing — matches Linear/VSCode convention
    const onCmdK = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSwitcherOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keydown', onCmdK);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keydown', onCmdK);
    };
  }, [openTicket, newModalStatus]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center text-sm text-slate-500">
      Loading tickets…
    </div>
  );
  if (err) return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center text-sm text-red-600 font-medium gap-3">
      {err}
      <button onClick={refresh} className="text-xs text-slate-600 underline">Retry</button>
    </div>
  );

  // Counts shown as badges on the tab buttons
  const counts = useMemo(() => {
    const open = tickets.filter((t) => t.status !== 'done').length;
    const myOpen = tickets.filter((t) => t.status !== 'done' && t.assignee === author).length;
    return { total: tickets.length, open, myOpen };
  }, [tickets, author]);

  const TabButton = ({ v, label }: { v: View; label: string }) => {
    const active = view === v;
    const iconName = v;
    const titleMap: Record<View, string> = {
      overview: 'Overview (press G then O)',
      board: 'Board (press G then B)',
      list: 'List (press G then L)',
    };
    const badge =
      v === 'overview' ? counts.myOpen :
      v === 'board'    ? counts.open :
      v === 'list'     ? visible.length :
      0;
    return (
      <button
        onClick={() => setView(v)}
        className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${
          active ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900 hover:bg-white'
        }`}
        title={titleMap[v]}
      >
        <Icon name={iconName} size={14} color={active ? '#fff' : '#64748b'} />
        {label}
        {badge > 0 && (
          <span className={`ml-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded ${
            active ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-700'
          }`}>
            {badge}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Chrome */}
      <div className="border-b border-slate-200 bg-white sticky top-0 z-20">
        <div className="max-w-[1500px] mx-auto px-6 py-3 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 shrink-0 mr-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-extrabold text-xs">T</div>
            <div>
              <div className="text-sm font-extrabold text-slate-900 leading-none tracking-tight">TreadAI</div>
              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mt-0.5">Project</div>
            </div>
          </div>

          <div className="bg-slate-100 rounded-lg p-1 flex gap-0.5">
            <TabButton v="overview" label="Overview" />
            <TabButton v="board" label="Board" />
            <TabButton v="list" label="List" />
          </div>

          <button
            onClick={() => setSwitcherOpen(true)}
            className="hidden md:flex items-center gap-2 text-xs text-slate-500 hover:text-slate-900 border border-slate-200 rounded-md px-2 py-1.5 bg-slate-50 hover:bg-white"
            title="Quick switcher"
          >
            <Icon name="search" size={12} />
            Jump to…
            <kbd className="font-mono text-[9px] bg-white border border-slate-200 px-1 rounded">⌘K</kbd>
          </button>

          <div className="flex-1 min-w-[180px] relative">
            <div className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
              <Icon name="search" size={14} color="#94a3b8" />
            </div>
            <input
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search… (press /)"
              className="w-full text-sm bg-slate-50 border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 focus:outline-none focus:border-slate-900 focus:bg-white"
            />
          </div>

          <button
            onClick={() => setOnlyMine((v) => !v)}
            className={`text-xs font-semibold px-2.5 py-1.5 rounded-md border transition-colors ${
              onlyMine
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
            }`}
            title="Show only tickets assigned to me"
          >
            Only mine
          </button>

          <select
            value={filterAssignee}
            onChange={(e) => setFilterAssignee(e.target.value)}
            className="text-xs font-semibold border border-slate-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:border-slate-900"
          >
            <option value="all">All assignees</option>
            {assignees.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value as any)}
            className="text-xs font-semibold border border-slate-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:border-slate-900"
          >
            <option value="all">All priorities</option>
            {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>

          <button
            onClick={() => downloadCsv(visible)}
            className="text-xs font-semibold border border-slate-200 text-slate-700 bg-white hover:border-slate-400 px-2.5 py-1.5 rounded-md flex items-center gap-1.5"
            title="Download visible tickets as CSV"
            disabled={visible.length === 0}
          >
            <Icon name="download" size={13} />
            Export
          </button>

          <button
            onClick={() => setNewModalStatus('backlog')}
            className="text-sm font-semibold bg-slate-900 text-white px-3 py-1.5 rounded-md hover:bg-slate-800 flex items-center gap-1.5"
            title="New ticket (press C)"
          >
            <Icon name="plus" size={14} color="#fff" />
            New
          </button>

          {editingAuthor ? (
            <input
              autoFocus
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              onBlur={() => { localStorage.setItem(AUTHOR_KEY, author.trim() || 'Someone'); setEditingAuthor(false); }}
              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
              className="w-24 text-xs font-semibold border border-slate-300 rounded-md px-2 py-1.5"
            />
          ) : (
            <button
              onClick={() => setEditingAuthor(true)}
              className="w-7 h-7 rounded-full bg-slate-900 text-white text-[11px] font-bold flex items-center justify-center"
              title={`You are ${author} — click to change`}
            >
              {author.slice(0, 1).toUpperCase()}
            </button>
          )}
        </div>
        <div className="max-w-[1500px] mx-auto px-6 pb-2 flex items-center gap-3 text-[10px] text-slate-400 font-medium flex-wrap">
          <span><kbd className="font-mono bg-slate-100 px-1 rounded">⌘K</kbd> jump</span>
          <span><kbd className="font-mono bg-slate-100 px-1 rounded">C</kbd> new</span>
          <span><kbd className="font-mono bg-slate-100 px-1 rounded">/</kbd> search</span>
          <span><kbd className="font-mono bg-slate-100 px-1 rounded">1-4</kbd> move status</span>
          <span><kbd className="font-mono bg-slate-100 px-1 rounded">G</kbd> <kbd className="font-mono bg-slate-100 px-1 rounded">O</kbd>/<kbd className="font-mono bg-slate-100 px-1 rounded">B</kbd>/<kbd className="font-mono bg-slate-100 px-1 rounded">L</kbd> switch view</span>
          <span><kbd className="font-mono bg-slate-100 px-1 rounded">ESC</kbd> close</span>
        </div>
      </div>

      {view === 'overview' && (
        <Overview
          tickets={visible}
          author={author}
          onOpenTicket={setOpenTicket}
          onSwitchToBoard={() => setView('board')}
        />
      )}
      {view === 'board' && (
        <Board
          tickets={tickets}
          filteredTickets={visible}
          onOpenTicket={setOpenTicket}
          onDragStart={onDragStart}
          onColumnDrop={onColumnDrop}
          onNewInStatus={setNewModalStatus}
        />
      )}
      {view === 'list' && (
        <ListView tickets={visible} onOpenTicket={setOpenTicket} />
      )}

      {switcherOpen && (
        <QuickSwitcher
          tickets={tickets}
          onOpen={setOpenTicket}
          onClose={() => setSwitcherOpen(false)}
        />
      )}

      {openTicket && (
        <TicketDrawer
          ticket={openTicket}
          token={token}
          author={author}
          onClose={() => setOpenTicket(null)}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
        />
      )}
      {newModalStatus && (
        <NewTicketModal
          defaultStatus={newModalStatus}
          defaultAssignee={author}
          onClose={() => setNewModalStatus(null)}
          onCreate={handleCreate}
        />
      )}
    </div>
  );
}

// ============================================================
// Page shell
// ============================================================

export const Project = () => {
  const [token, setToken] = useState<string | null>(() => sessionStorage.getItem(TOKEN_KEY));
  if (!token) return <Gate onUnlock={setToken} />;
  return <Dashboard token={token} />;
};

export default Project;
